"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_DOCUMENT_ATTRIBUTE = exports.APP_BASE_PAGE_LEAVE_EVENT = exports.APP_BASE_PAGE_ENTER_EVENT = void 0;
exports.installAppBaseRouteNavigation = installAppBaseRouteNavigation;
const app_base_nav_mark_1 = require("./app-base-nav-mark");
/** Fired on `document` after a page of the App is shown, also once for the first document. */
exports.APP_BASE_PAGE_ENTER_EVENT = 'streamlain:page-enter';
/** Fired on `document` before the shown page of the App is replaced. */
exports.APP_BASE_PAGE_LEAVE_EVENT = 'streamlain:page-leave';
/** `<html>` attribute naming the delivered state; pages are only swapped within one state. */
exports.APP_BASE_DOCUMENT_ATTRIBUTE = 'data-sl-app-document';
/**
 * Changes pages of an App without loading a new document: the frame stays,
 * the page content is swapped and the address follows.
 */
function installAppBaseRouteNavigation(window, host) {
    const document = window.document;
    const stampAttribute = 'data-sl-app-document';
    const stamp = document.documentElement.getAttribute(stampAttribute);
    const withoutHash = (url) => {
        const parsed = new window.URL(url, document.baseURI);
        parsed.hash = '';
        return parsed.href;
    };
    /**
     * One page has one address here: an address without `lang` shows the
     * language of the document, so it names the same page as one that says it.
     */
    const pageOf = (address) => {
        const locale = document.documentElement.getAttribute('data-sl-binding-locale');
        const parsed = new window.URL(address, document.baseURI);
        if (locale && !parsed.searchParams.has('lang')) {
            parsed.searchParams.set('lang', locale);
        }
        return parsed.href;
    };
    const scriptAddresses = new Set();
    /** The file of a script address, without the version in its query. */
    const scriptFile = (src) => {
        const parsed = new window.URL(src);
        return parsed.origin + parsed.pathname;
    };
    /** Scripts this navigation added, until they ran: a later page waits for them. */
    const scriptLoads = new Map();
    const rememberScripts = () => {
        for (const script of Array.from(document.querySelectorAll('script[src]'))) {
            scriptAddresses.add(script.src);
        }
    };
    /**
     * The side navigation folds away with its toggle in the top bar; the choice
     * holds for every page and later visits of this origin. Storage a sandboxed
     * page may not use keeps the choice until the page loads again.
     */
    const sideKey = 'sl-frame-side-collapsed';
    const sideAttribute = 'data-sl-frame-side-collapsed';
    let sideCollapsed = false;
    const showSide = (collapsed) => {
        sideCollapsed = collapsed;
        if (collapsed)
            document.body.setAttribute(sideAttribute, '');
        else
            document.body.removeAttribute(sideAttribute);
        for (const toggle of Array.from(document.querySelectorAll('button[data-sl-frame-side-toggle]'))) {
            toggle.setAttribute('aria-expanded', String(!collapsed));
        }
    };
    const storedSide = () => {
        try {
            return window.localStorage.getItem(sideKey) === '1';
        }
        catch {
            return false;
        }
    };
    const toggleSide = () => {
        // Only a toggle slides the column; a page that loads folded stands still.
        document.body.setAttribute('data-sl-frame-side-motion', '');
        const collapsed = !sideCollapsed;
        showSide(collapsed);
        try {
            window.localStorage.setItem(sideKey, collapsed ? '1' : '0');
        }
        catch {
            // The choice holds for this page only.
        }
    };
    const scrollByAddress = new Map();
    const shownAddress = () => host.shownAddress?.() ?? window.location.href;
    let shown = withoutHash(shownAddress());
    // An App keeps view state in its address with history.replaceState: the
    // replaced address is the shown one from then on, so a link to the address
    // before it changes the page again.
    const history = window.history;
    const replaceState = history.replaceState;
    const replaceShownState = function (...args) {
        replaceState.apply(this, args);
        if (heading === null)
            shown = withoutHash(shownAddress());
    };
    history.replaceState = replaceShownState;
    let left = false;
    let run = 0;
    let pending = null;
    /** Address of the page change under way, `null` when none is. */
    let heading = null;
    const emit = (name, detail) => {
        document.dispatchEvent(new window.CustomEvent(name, { detail }));
    };
    // A page is announced only while the window shows it; one that became
    // ready while it was hidden is announced when it shows again.
    let hidden = false;
    let missed = null;
    const enter = (initial) => {
        // A window that no longer shows this document announces nothing.
        if (window.document !== document)
            return;
        if (hidden) {
            missed = () => enter(initial);
            return;
        }
        left = false;
        const url = shownAddress();
        emit('streamlain:page-enter', {
            route: host.routeOf(url),
            url,
            initial,
        });
    };
    // A page change never stops halfway: what adopting a page failed with
    // reaches the window as an uncaught error, and the page is announced.
    const adoptPage = async () => {
        try {
            await host.adopt();
        }
        catch (error) {
            window.queueMicrotask(() => {
                throw error;
            });
        }
    };
    const settled = (element) => new Promise(resolve => {
        element.addEventListener('load', () => resolve(true), { once: true });
        element.addEventListener('error', () => resolve(false), { once: true });
    });
    const copyAttributes = (from, to) => {
        for (const name of to.getAttributeNames())
            to.removeAttribute(name);
        for (const name of from.getAttributeNames()) {
            to.setAttribute(name, from.getAttribute(name) ?? '');
        }
    };
    const swapChildren = (from, to) => {
        const nodes = Array.from(from.childNodes).map(node => document.importNode(node, true));
        to.replaceChildren(...nodes);
        // Scripts load once through the script list, never through the swap.
        for (const script of Array.from(to.querySelectorAll('script'))) {
            script.remove();
        }
    };
    /** The page swapped in last, until it is announced. */
    let unsettled = null;
    /**
     * Runs the scripts of the swapped page, adopts it and announces it. A
     * script an overtaken change added must have run before, or it misses
     * page-enter.
     */
    const settle = async (current, page) => {
        for (const { src, script } of page.scripts) {
            if (scriptAddresses.has(src)) {
                const loading = scriptLoads.get(src);
                if (loading && !(await loading)) {
                    host.loadWholeDocument(page.target);
                    return;
                }
                if (current !== run)
                    return;
                continue;
            }
            scriptAddresses.add(src);
            const added = document.createElement('script');
            for (const name of script.getAttributeNames()) {
                added.setAttribute(name, script.getAttribute(name) ?? '');
            }
            added.setAttribute('src', src);
            added.async = false;
            const loaded = settled(added);
            scriptLoads.set(src, loaded);
            document.head.append(added);
            if (!(await loaded)) {
                host.loadWholeDocument(page.target);
                return;
            }
            if (current !== run)
                return;
        }
        await adoptPage();
        if (current !== run)
            return;
        const anchor = page.hash
            ? document.getElementById(decodeURIComponent(page.hash.slice(1)))
            : null;
        if (anchor)
            anchor.scrollIntoView();
        else
            window.scrollTo(0, page.push ? 0 : (scrollByAddress.get(page.address) ?? 0));
        const focusTarget = document.querySelector('[data-sl-frame-outlet]') ??
            document.body;
        if (!focusTarget.hasAttribute('tabindex')) {
            focusTarget.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: true });
        unsettled = null;
        heading = null;
        enter(false);
    };
    const show = async (target, push) => {
        const address = withoutHash(target);
        const hash = new window.URL(target, document.baseURI).hash;
        if (pageOf(address) === pageOf(shown)) {
            if (heading !== null && pageOf(heading) !== pageOf(shown)) {
                // A change to another page is under way: it stops and the shown page
                // stays. Its scripts released it on leave, so it is announced again,
                // after it was set up if the change it came with was overtaken.
                const current = ++run;
                pending?.abort();
                pending = null;
                heading = null;
                (0, app_base_nav_mark_1.markAppBaseNavRoute)(document, host.routeOf(shown));
                if (unsettled) {
                    await settle(current, unsettled);
                    return;
                }
                enter(false);
                return;
            }
            if (!push)
                return;
            if (hash)
                window.location.hash = hash;
            else
                window.scrollTo(0, 0);
            return;
        }
        const current = ++run;
        heading = address;
        pending?.abort();
        const controller = new window.AbortController();
        pending = controller;
        if (!left) {
            left = true;
            emit('streamlain:page-leave', {
                from: host.routeOf(shown),
                to: host.routeOf(target),
            });
        }
        // The frame shows where the change goes while the next page loads.
        (0, app_base_nav_mark_1.markAppBaseNavRoute)(document, host.routeOf(target));
        scrollByAddress.set(shown, window.scrollY);
        const html = stamp === null
            ? null
            : await host.loadDocument(target, controller.signal).catch(() => null);
        if (current !== run)
            return;
        const next = html === null
            ? null
            : new window.DOMParser().parseFromString(html, 'text/html');
        if (!next || next.documentElement.getAttribute(stampAttribute) !== stamp) {
            host.loadWholeDocument(target);
            return;
        }
        const nextBase = new window.URL(next.querySelector('base[href]')?.getAttribute('href') ?? '', target).href;
        const absolute = (value) => new window.URL(value ?? '', nextBase).href;
        const sheetSelector = 'link[rel~="stylesheet"][href]';
        const liveSheets = new Map();
        for (const link of Array.from(document.querySelectorAll(sheetSelector))) {
            // The next page can change both the address and its <base>. Retained
            // stylesheets must keep the address from which they were loaded.
            const href = link.href;
            if (link.getAttribute('href') !== href)
                link.setAttribute('href', href);
            liveSheets.set(href, link);
        }
        const nextSheets = Array.from(next.querySelectorAll(sheetSelector));
        const keptSheets = new Set();
        const addedSheets = [];
        for (const link of nextSheets) {
            const href = absolute(link.getAttribute('href'));
            keptSheets.add(href);
            if (liveSheets.has(href))
                continue;
            const added = document.importNode(link, false);
            added.setAttribute('href', href);
            addedSheets.push(settled(added));
            document.head.append(added);
        }
        await Promise.all(addedSheets);
        if (current !== run)
            return;
        // Every script the window already ran, also one added after install.
        rememberScripts();
        // A new version of a script that already ran would run the App a second
        // time beside the first: the next page then loads as a whole document.
        const ranFiles = new Set(Array.from(scriptAddresses, scriptFile));
        const nextScripts = Array.from(next.querySelectorAll('script[src]'), script => absolute(script.getAttribute('src')));
        if (nextScripts.some(src => !scriptAddresses.has(src) && ranFiles.has(scriptFile(src)))) {
            host.loadWholeDocument(target);
            return;
        }
        if (push && !host.pushAddress(target)) {
            host.loadWholeDocument(target);
            return;
        }
        shown = address;
        host.commit?.(next);
        copyAttributes(next.documentElement, document.documentElement);
        copyAttributes(next.body, document.body);
        // Lists of the head (title, description) belong to their page as well.
        const managed = 'title, meta, base, style, template, link:not([rel~="stylesheet"])';
        for (const element of Array.from(document.head.querySelectorAll(managed))) {
            element.remove();
        }
        document.head.prepend(...Array.from(next.head.querySelectorAll(managed)).map(element => document.importNode(element, true)));
        for (const [href, link] of liveSheets) {
            if (!keptSheets.has(href))
                link.remove();
        }
        const outlet = document.querySelector('[data-sl-frame-outlet]');
        const nextOutlet = next.querySelector('[data-sl-frame-outlet]');
        const regionAttributes = [
            'data-sl-frame-brand',
            ...app_base_nav_mark_1.APP_BASE_FRAME_NAV_REGION_ATTRIBUTES,
            'data-sl-frame-foot-content',
            'data-sl-frame-panel',
            'data-sl-frame-side-toggle',
        ];
        const regionSelector = regionAttributes
            .map(attribute => '[' + attribute + ']')
            .join(', ');
        const navs = Array.from(document.querySelectorAll(regionSelector));
        const nextNavs = Array.from(next.querySelectorAll(regionSelector));
        const frameStays = outlet !== null &&
            nextOutlet !== null &&
            navs.length === nextNavs.length &&
            navs.every((region, index) => {
                const nextRegion = nextNavs[index];
                return (region.tagName === nextRegion.tagName &&
                    regionAttributes.every(attribute => region.getAttribute(attribute) ===
                        nextRegion.getAttribute(attribute)));
            });
        const page = {
            target,
            address,
            hash,
            push,
            scripts: Array.from(next.querySelectorAll('script[src]'), (script, index) => ({ src: nextScripts[index], script })),
        };
        if (frameStays) {
            swapChildren(nextOutlet, outlet);
            navs.forEach((nav, index) => {
                copyAttributes(nextNavs[index], nav);
                swapChildren(nextNavs[index], nav);
            });
        }
        else {
            swapChildren(next.body, document.body);
        }
        // The body takes the attributes of the new page and a new top bar names
        // its toggle as the server wrote it: both show the choice again.
        showSide(sideCollapsed);
        unsettled = page;
        await settle(current, page);
    };
    const onClick = (event) => {
        if (event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey) {
            return;
        }
        const origin = event.target;
        if (origin?.closest('button[data-sl-frame-side-toggle]')) {
            event.preventDefault();
            toggleSide();
            return;
        }
        const action = origin?.closest('button[data-sl-nav-action]');
        if (action) {
            const targetId = action.getAttribute('data-sl-nav-target') ?? '';
            const target = document.getElementById(targetId);
            if (!target) {
                event.preventDefault();
                window.console.warn('Navigation entry ' +
                    (action.getAttribute('data-sl-nav-id') ??
                        action.textContent?.trim() ??
                        '') +
                    ' target ' +
                    targetId +
                    ' is missing from the current page and frame');
                return;
            }
            const kind = action.getAttribute('data-sl-nav-action');
            if (kind === 'anchor') {
                event.preventDefault();
                target.scrollIntoView();
                if (!target.hasAttribute('tabindex'))
                    target.setAttribute('tabindex', '-1');
                target.focus({ preventScroll: true });
            }
            else if (kind === 'overlay') {
                event.preventDefault();
                // Reuse native top-layer overlays and their focus/Escape handling.
                if (target instanceof window.HTMLDialogElement) {
                    if (!target.open)
                        target.showModal();
                }
                else if (target.hasAttribute('popover'))
                    target.togglePopover();
                else
                    window.console.warn('Navigation overlay target ' +
                        targetId +
                        ' must be a dialog or popover');
            }
            return;
        }
        const link = origin?.closest('a[href]') ?? null;
        if (!link || link.hasAttribute('download'))
            return;
        const frame = link.getAttribute('target');
        if (frame && frame !== '_self')
            return;
        const raw = link.getAttribute('href') ?? '';
        if (raw.startsWith('#')) {
            // `<base href>` would send a bare hash link to another address.
            event.preventDefault();
            window.location.hash = raw;
            return;
        }
        const route = host.routeOf(link.href);
        if (route === null)
            return;
        event.preventDefault();
        // The host knows where a route lives, a link only names the route.
        const linked = new window.URL(link.href);
        void show(host.routeUrl(route + linked.search + linked.hash), true);
    };
    const onPopState = () => {
        void show(shownAddress(), false);
    };
    let firstEnterDone = false;
    const firstEnter = () => {
        if (firstEnterDone)
            return;
        firstEnterDone = true;
        showSide(storedSide());
        // The first document gets the same order as every later page.
        void adoptPage().then(() => {
            if (run === 0)
                enter(true);
        });
    };
    const onPageHide = () => {
        hidden = true;
    };
    const onPageShow = () => {
        hidden = false;
        const again = missed;
        missed = null;
        again?.();
    };
    // A panel or the more list of the frame takes the focus on its first
    // control as it opens: the page that holds it, not an autofocus attribute
    // the browser tries on every load, also in frames that may not focus.
    const onToggle = (event) => {
        const popover = event.target;
        if (event.newState !== 'open' ||
            !popover?.matches('[data-sl-frame-panel], [data-sl-frame-more]'))
            return;
        popover
            .querySelector('a,button,input,select,textarea,[tabindex]')
            ?.focus();
    };
    document.addEventListener('click', onClick);
    // Toggle does not bubble: the document hears it while it passes down.
    document.addEventListener('toggle', onToggle, true);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    if (document.readyState === 'complete') {
        window.queueMicrotask(firstEnter);
    }
    else {
        // Page scripts set their listener while the document loads.
        document.addEventListener('DOMContentLoaded', firstEnter, { once: true });
        window.addEventListener('load', firstEnter, { once: true });
    }
    return {
        navigate: path => show(host.routeUrl(path), true),
        dispose: () => {
            run += 1;
            pending?.abort();
            document.removeEventListener('click', onClick);
            document.removeEventListener('toggle', onToggle, true);
            window.removeEventListener('popstate', onPopState);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('pageshow', onPageShow);
            document.removeEventListener('DOMContentLoaded', firstEnter);
            window.removeEventListener('load', firstEnter);
            if (history.replaceState === replaceShownState) {
                history.replaceState = replaceState;
            }
        },
    };
}
